import React from 'react';
import { NavLink } from 'react-router-dom';
import { menuData } from '../../config/menuData.js';
import { useAuth } from '../../context/AuthContext.jsx';
import { canAccess } from '../../config/roles.js';

// Below this many total accessible pages, the whole nav renders as flat
// top-level links — a dropdown that opens to one or two items reads worse than
// just showing those items. Above it, pages stay grouped under dropdowns.
const FLATTEN_THRESHOLD = 4;

// Renders the shared top navigation from the menuData reference, filtered to
// the current user's role (config/roles.js PAGE_ROLES). Pages are grouped under
// Bootstrap dropdowns ONLY when it helps: a group with a single visible item —
// or a user with few total items — renders as direct links instead of a
// dropdown-of-one. A group with no accessible leaves is hidden. Bootstrap's
// bundled JS drives the dropdown behaviour.
export default function NavMenu() {
  const { user } = useAuth();

  // Filter each group's leaves to the pages this user may access, then drop
  // any group left with no visible items.
  const groups = menuData.children
    .map((group) => ({
      ...group,
      visibleLeaves: (group.children || []).filter((item) => canAccess(user, item.slug)),
    }))
    .filter((group) => group.visibleLeaves.length > 0);

  const totalLeaves = groups.reduce((n, g) => n + g.visibleLeaves.length, 0);
  const flattenAll = totalLeaves <= FLATTEN_THRESHOLD;

  const renderLeaf = (item) => (
    <li className="nav-item" key={item.slug}>
      <NavLink className="nav-link text-white" to={`/${item.slug}`}>
        {item.icon && <i className={`${item.icon} me-1`} aria-hidden="true" />}
        {item.label}
      </NavLink>
    </li>
  );

  const renderDropdown = (group) => {
    const id = `menu-${slugify(group.label)}`;
    return (
      <li className="nav-item dropdown" key={group.label}>
        <a
          className="nav-link dropdown-toggle text-white"
          href="#"
          id={id}
          role="button"
          data-bs-toggle="dropdown"
          aria-expanded="false"
        >
          {group.icon && <i className={`${group.icon} me-1`} aria-hidden="true" />}
          {group.label}
        </a>
        <ul className="dropdown-menu" aria-labelledby={id}>
          {group.visibleLeaves.map((item) => (
            <li key={item.slug}>
              <NavLink className="dropdown-item" to={`/${item.slug}`}>
                {item.icon && <i className={`${item.icon} me-2`} aria-hidden="true" />}
                {item.label}
              </NavLink>
            </li>
          ))}
        </ul>
      </li>
    );
  };

  const items = [];
  for (const group of groups) {
    if (flattenAll || group.visibleLeaves.length === 1) {
      group.visibleLeaves.forEach((leaf) => items.push(renderLeaf(leaf)));
    } else {
      items.push(renderDropdown(group));
    }
  }

  return <ul className="navbar-nav me-auto mb-2 mb-lg-0">{items}</ul>;
}

function slugify(label) {
  return label.replace(/\W+/g, '_').toLowerCase();
}
